import { defineStore } from "pinia";
import { Track } from "livekit-client";
import { markRaw } from "vue";
import { LOCAL_CAMERA_ID, LOCAL_SHARE_ID, DEFAULT_CONNECTION_QUALITY } from "%/apps/video-engine/constants";
import { useLivekitStore } from "%/apps/video-engine/stores/livekit-store";
import { isViewerParticipant } from "%/apps/video-engine/participant-roles";

export const useParticipantTilesStore = defineStore("participant-tiles-store", {
  state: () => ({
    tilesByPeerId: {},
    streamCache: Object.create(null),
  }),
  getters: {
    localCameraStream: state => state.tilesByPeerId[LOCAL_CAMERA_ID]?.stream || null,
    localScreenShareStream: state => state.tilesByPeerId[LOCAL_SHARE_ID]?.stream || null,
    remoteTiles: state => {
      const map = {};
      Object.entries(state.tilesByPeerId).forEach(([id, entry]) => {
        if (!entry || entry.isLocal) {
          return;
        }
        map[id] = entry;
      });
      return map;
    },
    rosterEntries(state) {
      const rows = [];
      const local = state.tilesByPeerId[LOCAL_CAMERA_ID];
      if (local) {
        rows.push({
          id: local.id,
          identity: local.identity,
          displayName: local.displayName,
          isLocal: true,
          isAudioMuted: Boolean(local.isAudioMuted),
          isVideoMuted: Boolean(local.isVideoMuted),
          handRaised: Boolean(local.handRaised),
          connectionQuality: local.connectionQuality,
        });
      }
      const remoteRows = [];
      Object.values(state.tilesByPeerId).forEach(tile => {
        if (!tile || tile.isLocal || tile.isScreenShare) {
          return;
        }
        remoteRows.push({
          id: tile.id,
          identity: tile.identity,
          audioTrackSid: tile.audioTrackSid,
          displayName: tile.displayName,
          isLocal: false,
          isAudioMuted: Boolean(tile.isAudioMuted),
          isVideoMuted: Boolean(tile.isVideoMuted),
          handRaised: Boolean(tile.handRaised),
          connectionQuality: tile.connectionQuality,
        });
      });
      remoteRows.sort((first, second) => Number(second.handRaised) - Number(first.handRaised));

      return rows.concat(remoteRows);
    },
    participantTilesInGridOrder(state) {
      const tiles = [];
      const localCamera = state.tilesByPeerId[LOCAL_CAMERA_ID];
      const localShare = state.tilesByPeerId[LOCAL_SHARE_ID];
      if (localCamera) {
        tiles.push(localCamera);
      }
      if (localShare?.stream) {
        tiles.push(localShare);
      }
      Object.values(this.remoteTiles).forEach(tile => {
        tiles.push(tile);
      });
      return tiles;
    },
  },
  actions: {
    syncFromLivekitRoom() {
      const livekit = useLivekitStore();
      const room = livekit.connection.room;
      const previousIndex = this.tilesByPeerId || {};
      const nextIndex = Object.create(null);

      if (!room) {
        this.resetTiles();
        return;
      }

      const localParticipant = room.localParticipant;
      if (localParticipant) {
        const cameraTile = this._buildTile(localParticipant, Track.Source.Camera, {
          isLocal: true,
          isScreenShare: false,
        }, previousIndex[LOCAL_CAMERA_ID]);
        if (cameraTile) {
          nextIndex[LOCAL_CAMERA_ID] = cameraTile;
        }
        if (livekit.isScreenSharing) {
          const shareTile = this._buildTile(localParticipant, Track.Source.ScreenShare, {
            isLocal: true,
            isScreenShare: true,
          }, previousIndex[LOCAL_SHARE_ID]);
          if (shareTile) {
            nextIndex[LOCAL_SHARE_ID] = shareTile;
          }
        }
      }

      room.remoteParticipants.forEach(participant => {
        if (isViewerParticipant(participant)) {
          return;
        }
        const cameraTile = this._buildTile(participant, Track.Source.Camera, {
          isLocal: false,
          isScreenShare: false,
        }, previousIndex[participant.sid]);
        if (cameraTile) {
          nextIndex[cameraTile.id] = cameraTile;
        }

        const shareTile = this._buildTile(participant, Track.Source.ScreenShare, {
          isLocal: false,
          isScreenShare: true,
        }, previousIndex[`${participant.sid}-share`]);
        if (shareTile) {
          nextIndex[shareTile.id] = shareTile;
        }
      });

      this.tilesByPeerId = nextIndex;
    },

    resetTiles() {
      this.tilesByPeerId = {};
      this.streamCache = Object.create(null);
    },

    _buildTile(participant, source, { isLocal, isScreenShare }, previousTile = null) {
      if (!participant) {
        return null;
      }

      const baseId = isLocal ? isScreenShare ? LOCAL_SHARE_ID : LOCAL_CAMERA_ID : participant.sid;
      const tileId = isLocal ? baseId : `${participant.sid}${isScreenShare ? "-share" : ""}`;
      const publication = participant.getTrackPublication?.(source) || null;
      const videoTrack = this._extractVideoTrack(publication);

      let audioPublication = null;
      if (isScreenShare) {
        audioPublication = participant.getTrackPublication?.(Track.Source.ScreenShareAudio) || null;
      } else if (!isLocal) {
        audioPublication = participant.getTrackPublication?.(Track.Source.Microphone) || null;
      }
      const audioTrack = this._extractAudioTrack(audioPublication);

      if (!videoTrack && !audioTrack) {
        if (isScreenShare) {
          return null;
        }
        const publicationIsLive = Boolean(publication) && !publication.isMuted;
        const audioPublicationIsLive = Boolean(audioPublication) && !audioPublication.isMuted;
        if (previousTile && audioPublicationIsLive && previousTile.audioTrack && !previousTile.videoTrack) {
          return {
            ...previousTile,
            participantSid: participant.sid,
            identity: participant.identity || null,
            handRaised: participant?.attributes?.handRaised === "1",
            audioTrackSid: audioPublication?.trackSid || null,
            connectionQuality: this.getConnectionQualityClass(participant),
            displayName: this.resolveTileDisplayName(participant, { isLocal, isScreenShare }),
            ...this._readMuteState(participant, { isLocal, isScreenShare }),
          };
        }
        if (previousTile && publicationIsLive && previousTile.videoTrack) {
          return {
            ...previousTile,
            participantSid: participant.sid,
            identity: participant.identity || null,
            handRaised: participant?.attributes?.handRaised === "1",
            audioTrackSid: audioPublication?.trackSid || null,
            connectionQuality: this.getConnectionQualityClass(participant),
            displayName: this.resolveTileDisplayName(participant, { isLocal, isScreenShare }),
            ...this._readMuteState(participant, { isLocal, isScreenShare }),
          };
        }
        const emptyStream = this._buildOrReuseMediaStream(tileId, []);
        if (previousTile) {
          return {
            ...previousTile,
            participantSid: participant.sid,
            identity: participant.identity || null,
            handRaised: participant?.attributes?.handRaised === "1",
            audioTrackSid: audioPublication?.trackSid || null,
            connectionQuality: this.getConnectionQualityClass(participant),
            displayName: this.resolveTileDisplayName(participant, { isLocal, isScreenShare }),
            stream: emptyStream,
            videoTrack: null,
            audioTrack: null,
            ...this._readMuteState(participant, { isLocal, isScreenShare }),
          };
        }
      }

      const tracksForStream = [];
      if (videoTrack) {
        tracksForStream.push(videoTrack);
      }
      if (!isLocal && audioTrack) {
        tracksForStream.push(audioTrack);
      }

      const stream = this._buildOrReuseMediaStream(tileId, tracksForStream);
      const displayName = this.resolveTileDisplayName(participant, { isLocal, isScreenShare });

      const nextTile = {
        id: tileId,
        peerId: tileId,
        participantSid: participant.sid,
        // Moderation targets the identity we minted (user:{id}); sids regenerate on reconnect.
        identity: participant.identity || null,
        handRaised: participant?.attributes?.handRaised === "1",
        audioTrackSid: audioPublication?.trackSid || null,
        isLocal,
        isScreenShare,
        displayName,
        stream: stream ? markRaw(stream) : null,
        videoTrack: videoTrack ? markRaw(videoTrack) : null,
        audioTrack: audioTrack ? markRaw(audioTrack) : null,
        connectionQuality: this.getConnectionQualityClass(participant),
        ...this._readMuteState(participant, { isLocal, isScreenShare }),
      };

      return previousTile
        ? {
          ...previousTile,
          ...nextTile,
        }
        : nextTile;
    },

    _readVideoPausedState(participant, source) {
      const publication = participant?.getTrackPublication?.(source);
      if (!publication || publication.isMuted) {
        return false;
      }

      return publication.streamState === "paused";
    },
    _readMuteState(participant, { isLocal, isScreenShare }) {
      if (isScreenShare) {
        return { isAudioMuted: false, isVideoMuted: false };
      }

      return {
        isAudioMuted: participant?.isMicrophoneEnabled === false,
        isVideoMuted: participant?.isCameraEnabled === false,
        isVideoPaused: !isLocal && this._readVideoPausedState(participant, Track.Source.Camera),
        isLocalMuteState: isLocal,
      };
    },

    _extractVideoTrack(publication) {
      if (!publication) {
        return null;
      }
      if (publication.videoTrack) {
        return publication.videoTrack;
      }
      if (publication.track?.kind === "video") {
        return publication.track;
      }
      return null;
    },

    _extractAudioTrack(publication) {
      if (!publication) {
        return null;
      }
      if (publication.audioTrack) {
        return publication.audioTrack;
      }
      if (publication.track?.kind === "audio") {
        return publication.track;
      }
      return null;
    },

    _buildOrReuseMediaStream(cacheKey, tracks = []) {
      if (typeof window === "undefined" || typeof MediaStream === "undefined") {
        return null;
      }

      const mediaTracks = tracks
          .map(track => {
            if (!track) {
              return null;
            }
            if (track.mediaStreamTrack) {
              return track.mediaStreamTrack;
            }
            if (track.track?.mediaStreamTrack) {
              return track.track.mediaStreamTrack;
            }
            if (typeof track.readyState === "string") {
              return track;
            }
            return null;
          })
          .filter(Boolean);

      if (!mediaTracks.length) {
        const cached = this.streamCache && this.streamCache[cacheKey];
        if (cached) {
          cached.getTracks().forEach(track => cached.removeTrack(track));
        }
        return null;
      }

      if (!this.streamCache) {
        this.streamCache = Object.create(null);
      }

      const existing = this.streamCache[cacheKey];
      if (existing) {
        const currentTracks = existing.getTracks();
        currentTracks.forEach(track => {
          if (!mediaTracks.includes(track)) {
            existing.removeTrack(track);
          }
        });
        mediaTracks.forEach(track => {
          if (!currentTracks.includes(track)) {
            existing.addTrack(track);
          }
        });
        return existing;
      }

      const stream = new MediaStream();
      mediaTracks.forEach(track => stream.addTrack(track));
      const rawStream = markRaw(stream);
      this.streamCache[cacheKey] = rawStream;
      return rawStream;
    },

    deriveBaseParticipantSid(peerId) {
      if (!peerId) {
        return peerId;
      }
      if (peerId === LOCAL_CAMERA_ID || peerId === LOCAL_SHARE_ID) {
        return LOCAL_CAMERA_ID;
      }
      if (peerId.endsWith("-share")) {
        return peerId.slice(0, -6);
      }
      return peerId;
    },

    resolveTileDisplayName(participant, { isLocal, isScreenShare }) {
      if (isLocal) {
        return isScreenShare ? "You (presentation)" : "You";
      }
      const baseName = participant?.name || participant?.identity || "Participant";
      return isScreenShare ? `${baseName}'s Screen` : baseName;
    },

    getConnectionQualityClass(participant) {
      if (!participant || !participant.connectionQuality) {
        return DEFAULT_CONNECTION_QUALITY;
      }
      return String(participant.connectionQuality).toLowerCase();
    },

    getDisplayNameForPeer(peerId) {
      if (!peerId) {
        return "Participant";
      }
      if (peerId === LOCAL_CAMERA_ID) {
        return "You";
      }
      if (peerId === LOCAL_SHARE_ID) {
        return "You (presentation)";
      }

      const entry = this.tilesByPeerId[peerId];
      if (entry?.displayName) {
        return entry.displayName;
      }

      const baseSid = this.deriveBaseParticipantSid(peerId);
      const livekit = useLivekitStore();
      const summary = livekit.remoteParticipants.find(participant => participant.sid === baseSid);
      if (!summary) {
        return "Participant";
      }
      if (peerId.endsWith("-share")) {
        return `${summary.name || summary.identity || "Participant"}'s Screen`;
      }
      return summary.name || summary.identity || "Participant";
    },

    hashParticipantIdentifier(value) {
      const input = String(value || "");
      let hash = 0;
      for (let i = 0; i < input.length; i += 1) {
        hash = (hash << 5) - hash + input.charCodeAt(i);
        hash |= 0;
      }
      return Math.abs(hash);
    },

    getParticipantColor(id) {
      const hue = this.hashParticipantIdentifier(id) % 360;
      return `hsl(${hue}, 70%, 45%)`;
    },

    getParticipantInitial(id) {
      const label = this.getDisplayNameForPeer(id);
      return (label || "?").charAt(0).toUpperCase();
    },
  },
});
